import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface SmartAlert {
  id: string;
  title: string;
  description: string;
  severity: "info" | "warning" | "critical";
  categoryId?: string;
  categoryName?: string;
  categoryColor?: string;
  isDismissed: boolean;
  createdAt: string;
}

export function useSmartAlerts() {
  const queryClient = useQueryClient();
  const today = new Date();

  // Fetch existing alerts from database
  const { data: dbAlerts = [], isLoading: isLoadingDbAlerts } = useQuery({
    queryKey: ["smart-alerts"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("trend_alerts")
        .select(`
          id,
          title,
          description,
          severity,
          category_id,
          is_dismissed,
          created_at,
          category:categories(name, color)
        `)
        .eq("is_dismissed", false)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return data.map(alert => ({
        id: alert.id,
        title: alert.title,
        description: alert.description,
        severity: alert.severity as "info" | "warning" | "critical",
        categoryId: alert.category_id,
        categoryName: alert.category?.name,
        categoryColor: alert.category?.color,
        isDismissed: alert.is_dismissed,
        createdAt: alert.created_at,
      }));
    },
  });

  // Calculate dynamic alerts based on transaction patterns via RPC
  const { data: calculatedAlerts = [], isLoading: isLoadingCalc } = useQuery({
    queryKey: ["calculated-alerts"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase.rpc("get_spending_alerts");

      if (error) throw error;

      const rpcAlerts = (data as any[]) || [];
      return rpcAlerts.map((a: any) => ({
        id: a.id,
        title: a.title,
        description: a.description,
        severity: a.severity as "info" | "warning" | "critical",
        categoryId: a.categoryId,
        categoryName: a.categoryName,
        categoryColor: a.categoryColor,
        isDismissed: false,
        createdAt: new Date().toISOString(),
      }));
    },
  });

  // Dismiss alert mutation
  const dismissAlert = useMutation({
    mutationFn: async (alertId: string) => {
      // Only dismiss database alerts
      if (alertId.startsWith("calc-")) {
        return; // Calculated alerts are just hidden from view
      }

      const { error } = await supabase
        .from("trend_alerts")
        .update({ is_dismissed: true })
        .eq("id", alertId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smart-alerts"] });
      toast({ title: "Alerta dispensado" });
    },
    onError: () => {
      toast({ title: "Erro ao dispensar alerta", variant: "destructive" });
    },
  });

  // Combine database alerts with calculated alerts
  const allAlerts = [...dbAlerts, ...calculatedAlerts];

  return {
    alerts: allAlerts,
    isLoading: isLoadingDbAlerts || isLoadingCalc,
    dismissAlert: dismissAlert.mutate,
    isDismissing: dismissAlert.isPending,
  };
}
